import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowDownUp, RefreshCw } from "lucide-react";
import { useUser } from "../lib/UserContext";
import { supabase } from "../lib/supabase";
import {
  formatMissionControlBotResponseRate,
  formatMissionControlLastPatientMessage,
  getMissionControlFreshness,
  sortMissionControlRows,
} from "../lib/missionControl.mjs";

type Clinic = {
  id: string;
  raison_sociale: string | null;
  bot_disabled_clinic: boolean | null;
  pdpa_consent_messages_enabled: boolean | null;
  rbac_enabled: boolean | null;
};

type ActivityStats = {
  clinic_id: string;
  messages_24h: number;
  messages_7d: number;
  last_patient_message_at: string | null;
  bot_response_rate: number | null;
};

type MissionControlRow = Clinic & ActivityStats;
type SortColumn = "messages_24h" | "messages_7d" | "last_patient_message_at" | "bot_response_rate";
type SortDirection = "asc" | "desc";

const freshnessClasses = {
  fresh: "text-emerald-700 bg-emerald-50",
  warning: "text-amber-700 bg-amber-50",
  stale: "text-red-700 bg-red-50",
  never: "text-red-700 bg-red-50",
};

function MissionControlSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-8 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-56 mb-3" />
      <div className="h-4 bg-gray-100 rounded w-96 max-w-full mb-8" />
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={index} className="h-14 border-b border-gray-100 flex items-center gap-5 px-5">
            <div className="h-3 bg-gray-200 rounded w-44" />
            <div className="h-3 bg-gray-200 rounded w-16" />
            <div className="h-3 bg-gray-200 rounded w-16" />
            <div className="h-3 bg-gray-200 rounded w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MissionControl() {
  const user = useUser();
  const [rows, setRows] = useState<MissionControlRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>("last_patient_message_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const loadMissionControl = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("No session");

      const [clientsResult, activityResponse] = await Promise.all([
        supabase
          .from("clients")
          .select("id, raison_sociale, bot_disabled_clinic, pdpa_consent_messages_enabled, rbac_enabled"),
        fetch(`${import.meta.env.VITE_API_URL}/admin/mission-control`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (clientsResult.error) throw clientsResult.error;
      if (!activityResponse.ok) throw new Error(`HTTP ${activityResponse.status}`);

      const stats = (await activityResponse.json()) as ActivityStats[];
      const statsByClinic = new Map(stats.map((stat) => [stat.clinic_id, stat]));
      setRows(
        (clientsResult.data ?? []).map((clinic) => ({
          ...clinic,
          clinic_id: clinic.id,
          messages_24h: 0,
          messages_7d: 0,
          last_patient_message_at: null,
          bot_response_rate: null,
          ...statsByClinic.get(clinic.id),
        })),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMissionControl();
  }, [loadMissionControl]);

  const sortedRows = useMemo(
    () => sortMissionControlRows(rows, sortColumn, sortDirection),
    [rows, sortColumn, sortDirection],
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === "last_patient_message_at" ? "asc" : "desc");
  };

  if (user === undefined) {
    return <MissionControlSkeleton />;
  }

  if (user === null || user.role !== "admin") {
    return (
      <div className="text-center text-gray-500 py-10">
        ⛔ Access restricted to administrators.
      </div>
    );
  }

  if (loading) return <MissionControlSkeleton />;

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-gray-500">
          <AlertCircle className="w-10 h-10 text-red-400" />
          <p className="text-sm">Failed to load mission control.</p>
          <button
            onClick={loadMissionControl}
            className="flex items-center gap-2 text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sortableHeader = (label: string, column: SortColumn) => (
    <button
      type="button"
      onClick={() => handleSort(column)}
      className="inline-flex items-center gap-1 hover:text-slate-900"
    >
      {label}
      <ArrowDownUp className={`w-3.5 h-3.5 ${sortColumn === column ? "text-primary" : "text-slate-400"}`} />
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 font-sans text-gray-800">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Mission control</h1>
          <p className="text-sm text-gray-500 mt-1">Vue transverse de l’activité et de la configuration des cliniques.</p>
        </div>
        <button
          onClick={loadMissionControl}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition"
          title="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left font-semibold px-5 py-3">Raison sociale</th>
              <th className="text-right font-semibold px-5 py-3">{sortableHeader("Messages reçus (24h)", "messages_24h")}</th>
              <th className="text-right font-semibold px-5 py-3">{sortableHeader("Messages reçus (7j)", "messages_7d")}</th>
              <th className="text-left font-semibold px-5 py-3">{sortableHeader("Dernier message patient", "last_patient_message_at")}</th>
              <th className="text-right font-semibold px-5 py-3">{sortableHeader("Taux de réponse bot", "bot_response_rate")}</th>
              <th className="text-left font-semibold px-5 py-3">Statuts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedRows.map((row) => {
              const freshness = getMissionControlFreshness(row.last_patient_message_at);
              const botEnabled = row.bot_disabled_clinic !== true;
              const pdpaEnabled = row.pdpa_consent_messages_enabled ?? true;
              const rbacEnabled = row.rbac_enabled === true;
              return (
                <tr key={row.id} className="hover:bg-slate-50/70">
                  <td className="px-5 py-4 font-medium text-slate-800">{row.raison_sociale || "—"}</td>
                  <td className="px-5 py-4 text-right tabular-nums">{row.messages_24h}</td>
                  <td className="px-5 py-4 text-right tabular-nums">{row.messages_7d}</td>
                  <td className="px-5 py-4">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${freshnessClasses[freshness]}`}>
                      {formatMissionControlLastPatientMessage(row.last_patient_message_at)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right tabular-nums">{formatMissionControlBotResponseRate(row.bot_response_rate)}</td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <span className={`rounded-full px-2.5 py-1 ${botEnabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>Bot : {botEnabled ? "actif" : "inactif"}</span>
                      <span className={`rounded-full px-2.5 py-1 ${pdpaEnabled ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>PDPA : {pdpaEnabled ? "actif" : "désactivé"}</span>
                      <span className={`rounded-full px-2.5 py-1 ${rbacEnabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>RBAC : {rbacEnabled ? "actif" : "inactif"}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {sortedRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-500">Aucune clinique trouvée.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-3">Taux de réponse bot : part des réponses assistant parmi les réponses assistant et humaines sur les 7 derniers jours.</p>
    </div>
  );
}
